import { GBasePass, GetBasePassUniformParametersList, Mesh } from "../../../basePass";
import { CreateTextureRT, ReadPixelsAsync } from "../../../core/resourcesUtils";
import { Shader } from "../../../core/shaderUtils";
import { GTexturePool } from "../../../core/texturePool";
import { GStack, Vector2, Vector3 } from "../../../core/types";
import { GTime, uint16ToFloat16 } from "../../../core/utils";
import { GLBindUniformBuffer, GLSetTexture, GLSetVec3 } from "../../../helpers/glHelper";
import { GetShaderSourceBasePassMainVS } from "../../../shaders/shaderBasePass";
import { CommonRenderingResources } from "../../../shaders/shaderConfig";
import {
    GetShaderSourceApplyFirePS,
    GetShaderSourceApplyFireVS,
    GetShaderSourceBasePassDestrPlanePS,
    GetShaderSourcePropagationPS,
} from "../../../shaders/shaderDestrPaintPlane";
import { ShaderSourceFullscreenPassVS } from "../../../shaders/shaderPostProcess";
import { SceneBase } from "../../scene";
import { PayneSceneDestrPainting } from "./destrPaintingMain";

function GetUniformParametersList(gl: WebGL2RenderingContext, shaderProgram: WebGLProgram) {
    const params = {
        FireTexture: gl.getUniformLocation(shaderProgram, "FireTexture"),
        ViewportRatio: gl.getUniformLocation(shaderProgram, "ViewportRatio"),
        PosPlaneSpace: gl.getUniformLocation(shaderProgram, "PosPlaneSpace"),
        NoiseTextureInterpolator: gl.getUniformLocation(shaderProgram, "NoiseTextureInterpolator"),
        NoiseTexture: gl.getUniformLocation(shaderProgram, "NoiseTexture"),

        EPaintingState: gl.getUniformLocation(shaderProgram, "EPaintingState"),
        PaintBrightnessScale: gl.getUniformLocation(shaderProgram, "PaintBrightnessScale"),

        SceneUniformBuffer: gl.getUniformBlockIndex(shaderProgram, "SceneUniformBuffer"),
    };
    return params;
}

export class GPUReadData {
    CurValueCPUArrBuffer = new Uint16Array(1);
    CurValueCPU = 0.0;
    ReadbackBuffer;

    StateRef;

    constructor(gl: WebGL2RenderingContext) {
        this.ReadbackBuffer = gl.createBuffer();
        gl.bindBuffer(gl.PIXEL_PACK_BUFFER, this.ReadbackBuffer);
        gl.bufferData(gl.PIXEL_PACK_BUFFER, this.CurValueCPUArrBuffer.byteLength, gl.STREAM_READ);
        gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);

        this.StateRef = { bReadingPixels: false }; // Async state object
    }
}

export class DestrPaintPlane {
    Size;
    FireTexture;
    FireFuelFrameBuffer;
    PingPongTextureCurIndex = 0;

    FireTextureHighestMIPFrameBuffer;
    //FuelTextureHighestMIPFrameBuffer;
    FireValueReadData: GPUReadData;
    //FuelValueReadData: GPUReadData;

    NoiseTextureInterpolator = 0;
    PaintBrightnessScale = 1;

    NoiseTexture: WebGLTexture;
    NoiseTextureLQ: WebGLTexture;

    //Shaders
    ShaderPropagation: Shader;
    ShaderBasePass: Shader;
    ShaderApplyFire: Shader;

    //Mesh
    RenderMesh = new Mesh();

    constructor(gl: WebGL2RenderingContext, inRenderTargetSize: Vector2, scene: PayneSceneDestrPainting) {
        this.Size = inRenderTargetSize;

        this.FireFuelFrameBuffer = [];
        this.FireTexture = [];

        for (let i = 0; i < 2; i++) {
            this.FireTexture[i] = CreateTextureRT(gl, inRenderTargetSize, gl.R16F, gl.RED, gl.HALF_FLOAT, true);
            this.FireFuelFrameBuffer[i] = gl.createFramebuffer();
            gl.bindFramebuffer(gl.FRAMEBUFFER, this.FireFuelFrameBuffer[i]);
            gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.FireTexture[i], 0);

            gl.drawBuffers([gl.COLOR_ATTACHMENT0]);
        }

        //To read fire value
        {
            const highestMipLevel = Math.log2(inRenderTargetSize.x);
            this.FireTextureHighestMIPFrameBuffer = [];
            //this.FuelTextureHighestMIPFrameBuffer = [];
            for (let i = 0; i < 2; i++) {
                this.FireTextureHighestMIPFrameBuffer[i] = gl.createFramebuffer();
                gl.bindFramebuffer(gl.FRAMEBUFFER, this.FireTextureHighestMIPFrameBuffer[i]);
                gl.framebufferTexture2D(
                    gl.READ_FRAMEBUFFER,
                    gl.COLOR_ATTACHMENT0,
                    gl.TEXTURE_2D,
                    this.FireTexture[i],
                    highestMipLevel,
                );
            }
            this.FireValueReadData = new GPUReadData(gl);
        }

        this.ShaderPropagation = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourcePropagationPS(),
            GetUniformParametersList,
        );

        this.ShaderApplyFire = new Shader(
            gl,
            GetShaderSourceApplyFireVS(),
            GetShaderSourceApplyFirePS(),
            GetUniformParametersList,
        );

        this.ShaderBasePass = new Shader(
            gl,
            GetShaderSourceBasePassMainVS(),
            GetShaderSourceBasePassDestrPlanePS(scene.SceneSDFDescShootingScene!),
            GetBasePassUniformParametersList,
        );

        this.NoiseTexture = GTexturePool.CreateTexture(gl, false, "perlinNoise1024");
        this.NoiseTextureLQ = GTexturePool.CreateTexture(gl, false, "perlinNoise32");
    }

    LoadCurValuesToCPU(gl: WebGL2RenderingContext) {
        const curFireTextureFramebuffer = this.FireTextureHighestMIPFrameBuffer[this.PingPongTextureCurIndex];
        gl.bindFramebuffer(gl.FRAMEBUFFER, curFireTextureFramebuffer);
        ReadPixelsAsync(
            gl,
            this.FireValueReadData.ReadbackBuffer!,
            0,
            0,
            1,
            1,
            gl.RED,
            gl.HALF_FLOAT,
            this.FireValueReadData.CurValueCPUArrBuffer,
            this.FireValueReadData.StateRef,
        );
        this.FireValueReadData.CurValueCPU = uint16ToFloat16(this.FireValueReadData.CurValueCPUArrBuffer.at(0)!);
    }

    OnUpdate(gl: WebGL2RenderingContext, scene: PayneSceneDestrPainting) {
        {
            const curSourceIndex = this.PingPongTextureCurIndex;
            const curShader = this.ShaderPropagation;

            gl.viewport(0, 0, this.Size.x, this.Size.y);

            gl.bindFramebuffer(gl.FRAMEBUFFER, this.FireFuelFrameBuffer[1 - curSourceIndex]);

            /* const drawBuffers = [gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1];
            gl.drawBuffers(drawBuffers); */

            gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

            gl.useProgram(curShader.ShaderProgram);

            GLBindUniformBuffer(
                gl,
                SceneBase.UniformBuffer.BufferGPU!,
                curShader.ShaderProgram,
                curShader.UniformParametersLocationList.SceneUniformBuffer,
            );

            const NoiseTextureInterpolatorSpeed = 0.25;
            const NoiseTextureInterpolatorMax = 3;
            this.NoiseTextureInterpolator += NoiseTextureInterpolatorSpeed * GTime.Delta * GTime.DeltaTimeScale;
            this.NoiseTextureInterpolator = this.NoiseTextureInterpolator % NoiseTextureInterpolatorMax;
            gl.uniform1f(
                curShader.UniformParametersLocationList.NoiseTextureInterpolator,
                this.NoiseTextureInterpolator,
            );

            const curState: number = scene.PaintState;
            gl.uniform1i(curShader.UniformParametersLocationList.EPaintingState, curState);

            //Textures
            gl.activeTexture(gl.TEXTURE0 + 2);
            gl.bindTexture(gl.TEXTURE_2D, this.FireTexture[curSourceIndex]);
            gl.uniform1i(curShader.UniformParametersLocationList.FireTexture, 2);

            gl.activeTexture(gl.TEXTURE0 + 4);
            gl.bindTexture(gl.TEXTURE_2D, this.NoiseTexture);
            gl.uniform1i(curShader.UniformParametersLocationList.NoiseTexture, 4);

            gl.drawArrays(gl.TRIANGLES, 0, 3);

            /* gl.drawBuffers([gl.COLOR_ATTACHMENT0]); */

            this.PingPongTextureCurIndex = 1 - this.PingPongTextureCurIndex;
        }
    }

    BindFireRT(gl: WebGL2RenderingContext) {
        const curSourceIndex = this.PingPongTextureCurIndex;
        gl.viewport(0, 0, this.Size.x, this.Size.y);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.FireFuelFrameBuffer[curSourceIndex]);
        // Set draw buffers
        const drawBuffers = [gl.COLOR_ATTACHMENT0];
        gl.drawBuffers(drawBuffers);
    }

    Reset(gl: WebGL2RenderingContext) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.FireFuelFrameBuffer[0]);
        gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);
        //Fill Fuel texture with 1.f
        const clearColor0 = new Float32Array([0.0, 0.0, 0.0, 0.0]);
        const clearColor1 = new Float32Array([1.0, 1.0, 1.0, 1.0]);
        gl.clearBufferfv(gl.COLOR, 0, clearColor0);
        gl.clearBufferfv(gl.COLOR, 1, clearColor1);

        gl.bindFramebuffer(gl.FRAMEBUFFER, this.FireFuelFrameBuffer[1]);
        gl.clearBufferfv(gl.COLOR, 0, clearColor0);
        gl.clearBufferfv(gl.COLOR, 1, clearColor1);
        gl.drawBuffers([gl.COLOR_ATTACHMENT0]);
    }

    ApplyFire(gl: WebGL2RenderingContext, posWS: Vector3, scene: PayneSceneDestrPainting) {
        this.BindFireRT(gl);

        const curShader = this.ShaderApplyFire;
        const uniformLoc = curShader.UniformParametersLocationList;

        /* Set up blending */
        gl.enable(gl.BLEND);
        gl.blendEquation(gl.MAX);
        gl.blendFunc(gl.ONE, gl.ONE);

        gl.bindVertexArray(CommonRenderingResources.PlaneShapeVAO);

        gl.useProgram(curShader.ShaderProgram);

        gl.uniform1f(uniformLoc.ViewportRatio, this.Size.x / this.Size.y);
        const curState: number = scene.PaintState;
        gl.uniform1i(curShader.UniformParametersLocationList.EPaintingState, curState);
        //transform from world space to tangent space

        const posPlane = GStack.TempVec3;
        posPlane.Set(posWS);
        posPlane.Negate(this.RenderMesh.Position);
        posPlane.x /= this.RenderMesh.Scale.x;
        posPlane.y /= this.RenderMesh.Scale.y;
        GLSetVec3(gl, uniformLoc.PosPlaneSpace, posPlane);

        GLBindUniformBuffer(
            gl,
            SceneBase.UniformBuffer.BufferGPU!,
            curShader.ShaderProgram,
            curShader.UniformParametersLocationList.SceneUniformBuffer,
        );

        {
            gl.activeTexture(gl.TEXTURE0 + 4);
            gl.bindTexture(gl.TEXTURE_2D, this.NoiseTexture);
            gl.uniform1i(uniformLoc.NoiseTexture, 4);
        }

        gl.drawArrays(gl.TRIANGLES, 0, 6);

        gl.disable(gl.BLEND);
    }

    Render(gl: WebGL2RenderingContext, envMap: WebGLTexture | null) {
        const inMesh = this.RenderMesh;
        const curShader = this.ShaderBasePass;

        if (!inMesh.bCanDraw) {
            return;
        }

        if (inMesh.Material.bAdditiveBlend) {
            // gl.enable(gl.BLEND);
            // gl.blendFunc(gl.ONE, gl.ONE);
            // gl.blendEquation(gl.FUNC_ADD);
        }

        const uniformsLocation = curShader.UniformParametersLocationList;

        gl.useProgram(curShader.ShaderProgram);

        gl.bindVertexArray(inMesh.VAO);

        gl.uniformMatrix4fv(uniformsLocation.LocalToWorldMatrix, false, inMesh.LocalToWorldMatrix.elementsSerialized);
        gl.uniformMatrix4fv(
            uniformsLocation.WorldToViewMatrix,
            false,
            SceneBase.WorldToCameraSpaceMatCPU.elementsSerialized,
        );

        gl.uniform1i(uniformsLocation.LightprobesGridSize, SceneBase.LightprobesManager.NumProbes.x);

        gl.uniform1f(curShader.UniformParametersLocationList.PaintBrightnessScale, this.PaintBrightnessScale);

        GLSetVec3(gl, uniformsLocation.PreTranslate, inMesh.InitBBoxCenter);
        gl.uniform1f(uniformsLocation.TextureScale, inMesh.Material.TextureScale);
        gl.uniform1f(uniformsLocation.Brightness, inMesh.Material.Brightness);
        gl.uniform1f(uniformsLocation.Metalness, inMesh.Material.Metalness);
        gl.uniform1i(uniformsLocation.bEmitter, inMesh.Material.bisEmmiter ? 1 : 0);

        let textureBindIndex = 1;
        GLSetTexture(gl, uniformsLocation.AlbedoTexture, inMesh.Material.AlbedoTexture, textureBindIndex++);
        GLSetTexture(gl, uniformsLocation.NormalTexture, inMesh.Material.NormalTexture, textureBindIndex++);
        GLSetTexture(gl, uniformsLocation.RoughnessTexture, inMesh.Material.RoughnessTexture, textureBindIndex++);

        if (envMap) {
            gl.activeTexture(gl.TEXTURE0 + textureBindIndex);
            gl.bindTexture(gl.TEXTURE_CUBE_MAP, envMap);
            gl.uniform1i(uniformsLocation.CubemapTexture, textureBindIndex);
            textureBindIndex++;
        }
        GLSetTexture(gl, uniformsLocation.BRDF_LUT, GBasePass.GInstance!.TextureIntegratedBRDF, textureBindIndex++);
        gl.uniform1i(uniformsLocation.EUseLightprobe, 0);
        gl.uniform1i(uniformsLocation.LightprobeIndex, 0);
        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.SHCoefficientsTexture,
            SceneBase.LightprobesManager.TextureSHCoeff,
            textureBindIndex++,
        );

        GLSetTexture(
            gl,
            uniformsLocation.FireTexture,
            this.FireTexture[this.PingPongTextureCurIndex],
            textureBindIndex++,
        );

        if (inMesh.NumIndices > 0) {
            gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, inMesh.IndexBufferGPU);
            gl.drawElements(gl.TRIANGLES, inMesh.NumIndices, inMesh.IndexBufferElementType, 0);
            //gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
        } else {
            gl.drawArrays(gl.TRIANGLES, 0, inMesh.NumVertices);
        }

        if (inMesh.Material.bAdditiveBlend) {
            gl.disable(gl.BLEND);
        }
    }
}
